import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import type { Project } from '@aimo/contracts/web';
import type { ApiClient } from '../../api.js';
import { relativeTime, visibleProjects, type ProjectFilter } from '../../stitch-landing.js';
import { Button } from '../button.js';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../dropdown-menu.js';
import { Icon } from '../icon.js';
import { Tooltip } from '../tooltip.js';
import { cn } from '../utils.js';
import { ProjectThumbnail } from './project-thumbnail.js';
import { text } from './messages.js';

const focusRing = 'outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1';
/** Graphite primary actions, shared with the home page so the library adds no color of its own. */
export const graphite = 'border-transparent bg-(--md-sys-color-inverse-surface) text-(--md-sys-color-inverse-on-surface) hover:bg-(--md-sys-color-inverse-surface)/90 disabled:bg-(--md-sys-color-surface-container-highest) disabled:text-(--md-sys-color-outline) disabled:opacity-100';
const FILTERS: Array<[ProjectFilter, string]> = [['recent', text.filterRecent], ['fav', text.filterFavorites], ['all', text.filterAll]];

export interface ProjectLibraryProps {
  projects: Project[];
  api: ApiClient | null;
  signedIn: boolean;
  favorites: ReadonlySet<string>;
  onToggleFavorite(projectId: string): void;
  onOpenProject(projectId: string): void;
  onCreateProject(): void;
  creating: boolean;
  /** Desktop only: the column is reduced to a rail. */
  collapsed: boolean;
  onCollapsedChange(collapsed: boolean): void;
  /** Narrow viewports show the library as a drawer over the page. */
  drawer: boolean;
  onCloseDrawer(): void;
}

function Brand() {
  return (
    <span className="flex items-center gap-1.5 font-heading text-[17px] font-medium tracking-tight text-foreground">
      <span aria-hidden className="text-primary">✦</span>AiMo <span className="font-normal text-muted-foreground">Studio</span>
    </span>
  );
}

/** The always-present project library: brand, create, search, filters and a list that scrolls on its own. */
export function ProjectLibrary(props: ProjectLibraryProps) {
  const { projects, collapsed, drawer } = props;
  if (collapsed && !drawer) {
    return (
      <nav aria-label={text.library} className="flex h-full w-16 shrink-0 flex-col items-center gap-3 bg-(--md-sys-color-surface) pb-4">
        <span aria-hidden className="flex h-16 items-center font-heading text-xl text-primary">✦</span>
        <Tooltip content={text.expandLibrary}>
          <Button variant="ghost" size="icon" className="size-10" aria-label={text.expandLibrary} aria-expanded={false} onClick={() => props.onCollapsedChange(false)}>
            <Icon name="left_panel_open" size={20} />
          </Button>
        </Tooltip>
        <Tooltip content={text.newProject}>
          <Button variant="default" size="icon" className={cn('size-10', graphite)} aria-label={text.newProjectButton} disabled={props.creating} onClick={props.onCreateProject}>
            <Icon name="add" size={20} />
          </Button>
        </Tooltip>
        <span className="mt-1 text-[11px] text-muted-foreground">{projects.length}<span className="sr-only">{text.projectCount}</span></span>
      </nav>
    );
  }
  return (
    <nav aria-label={text.library}
      className={cn('flex h-full w-[280px] shrink-0 flex-col bg-(--md-sys-color-surface)',
        drawer && 'fixed inset-y-0 left-0 z-50 w-[min(320px,86vw)] shadow-lg')}>
      <div className="flex h-16 shrink-0 items-center justify-between pl-5 pr-3">
        <Brand />
        {drawer
          ? <Button variant="ghost" size="icon" className="size-9" aria-label={text.closeLibrary} onClick={props.onCloseDrawer}><Icon name="close" size={20} /></Button>
          : <Tooltip content={text.collapseLibrary}>
              <Button variant="ghost" size="icon" className="size-9" aria-label={text.collapseLibrary} aria-expanded onClick={() => props.onCollapsedChange(true)}>
                <Icon name="left_panel_close" size={20} />
              </Button>
            </Tooltip>}
      </div>
      {/* The library floats as one rounded panel below the brand row, like the page's other surfaces. */}
      <div className="mx-3 mb-3 flex min-h-0 flex-1 flex-col rounded-2xl bg-(--md-sys-color-surface-container-low) pt-3">
        <LibraryBody {...props} />
      </div>
    </nav>
  );
}

function LibraryBody({ projects, api, signedIn, favorites, onToggleFavorite, onOpenProject, onCreateProject, creating }: ProjectLibraryProps) {
  const [filter, setFilter] = useState<ProjectFilter>('recent');
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const list = useRef<HTMLUListElement>(null);
  const visible = visibleProjects(projects, filter, query, favorites);
  const favoriteCount = projects.filter(project => favorites.has(project.projectId)).length;
  const current = Math.min(active, Math.max(0, visible.length - 1));
  useEffect(() => { setActive(0); }, [filter, query]);

  const move = (event: KeyboardEvent<HTMLUListElement>) => {
    const keys: Record<string, (index: number) => number> = { ArrowDown: index => index + 1, ArrowUp: index => index - 1, Home: () => 0, End: () => visible.length - 1 };
    const step = keys[event.key];
    if (!step || !(event.target instanceof HTMLElement) || !event.target.matches('[data-project-open]')) return;
    event.preventDefault();
    const next = Math.min(visible.length - 1, Math.max(0, step(current)));
    setActive(next);
    list.current?.querySelectorAll<HTMLElement>('[data-project-open]')[next]?.focus();
  };

  return (
    <>
      <div className="flex flex-col gap-3 px-4 pb-3">
        <Button variant="default" size="default" className={cn('h-10 w-full text-sm', graphite)} disabled={creating} onClick={onCreateProject}>{text.newProjectButton}</Button>
        <label className="flex h-9 items-center gap-2 rounded-full border border-transparent bg-(--md-sys-color-surface-container-high) px-3 text-muted-foreground focus-within:border-(--md-sys-color-outline-variant) focus-within:bg-card">
          <Icon name="search" size={18} />
          <input type="search" placeholder={text.search} aria-label={text.search} value={query} onChange={event => setQuery(event.target.value)}
            className="min-w-0 flex-1 border-0 bg-transparent font-sans text-[13px] text-foreground outline-none placeholder:text-muted-foreground" />
        </label>
        <div role="group" aria-label={text.filters} className="flex w-fit gap-0.5 rounded-full bg-(--md-sys-color-surface-container-high) p-0.5">
          {FILTERS.map(([key, label]) => (
            <button key={key} type="button" aria-pressed={filter === key} onClick={() => setFilter(key)}
              className={cn('h-7 cursor-pointer rounded-full border-0 px-3 font-sans text-xs transition-colors', focusRing,
                filter === key ? 'bg-card font-medium text-foreground shadow-sm' : 'bg-transparent text-muted-foreground hover:text-foreground')}>
              {key === 'fav' ? `${label} (${favoriteCount})` : label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-baseline justify-between px-5 pb-1.5 pt-1">
        <h2 id="aimo-library-heading" className="m-0 font-sans text-xs font-medium text-muted-foreground">{text.myProjects}</h2>
        <span className="text-[11px] text-muted-foreground">{projects.length}</span>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-4">
        {visible.length === 0
          ? <div className="flex flex-col gap-1 px-3 py-8 text-center text-[13px] text-muted-foreground">
              <span>{signedIn ? text.noMatches : text.signInToSee}</span>
              <small className="text-[11px]">{text.emptyHint}</small>
            </div>
          : <ul ref={list} aria-labelledby="aimo-library-heading" onKeyDown={move} className="m-0 flex list-none flex-col gap-0.5 p-0">
              {visible.map((project, index) => (
                <ProjectRow key={project.projectId} project={project} api={api} favorite={favorites.has(project.projectId)} tabbable={index === current}
                  onFocus={() => setActive(index)} onOpen={() => onOpenProject(project.projectId)} onToggleFavorite={() => onToggleFavorite(project.projectId)} />
              ))}
            </ul>}
      </div>
    </>
  );
}

function ProjectRow({ project, api, favorite, tabbable, onFocus, onOpen, onToggleFavorite }: { project: Project; api: ApiClient | null; favorite: boolean; tabbable: boolean; onFocus(): void; onOpen(): void; onToggleFavorite(): void }) {
  const favoriteLabel = `${favorite ? text.unfavorite : text.favorite} ${project.name}`;
  const updated = relativeTime(project.updatedAt);
  return (
    <li aria-label={project.name} className="group relative flex items-center rounded-xl hover:bg-accent has-[[data-project-open]:focus-visible]:bg-accent">
      <button type="button" data-project-open tabIndex={tabbable ? 0 : -1} onFocus={onFocus} onClick={onOpen} title={project.name}
        className={cn('flex min-w-0 flex-1 cursor-pointer items-center gap-3 rounded-xl border-0 bg-transparent py-2 pl-2 pr-16 text-left font-sans text-inherit', focusRing)}>
        <ProjectThumbnail project={project} api={api} className="size-10" />
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-[13px] font-medium text-foreground">{project.name}</span>
          <span className="truncate text-[11px] text-muted-foreground">{updated ? text.updated(updated) : text.enterCanvas}</span>
        </span>
      </button>
      <div className="absolute right-1.5 flex items-center gap-0.5">
        <button type="button" aria-label={favoriteLabel} aria-pressed={favorite} title={favoriteLabel} onClick={onToggleFavorite}
          className={cn('flex size-7 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent hover:bg-(--md-sys-color-surface-container-highest)', focusRing,
            favorite ? 'text-foreground' : 'text-muted-foreground opacity-0 group-hover:opacity-100 focus-visible:opacity-100 group-has-[[data-project-open]:focus-visible]:opacity-100')}>
          <Icon name={favorite ? 'star-fill' : 'star'} size={18} />
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" aria-label={text.moreActions(project.name)}
              className={cn('flex size-7 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent text-muted-foreground opacity-0 hover:bg-(--md-sys-color-surface-container-highest) group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100 group-has-[[data-project-open]:focus-visible]:opacity-100', focusRing)}>
              <Icon name="more_horiz" size={18} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" side="right" className="min-w-40">
            <DropdownMenuItem onSelect={onOpen}><Icon name="open_in_new" size={16} />{text.openProject}</DropdownMenuItem>
            <DropdownMenuItem onSelect={onToggleFavorite}><Icon name={favorite ? 'star' : 'star-fill'} size={16} />{favorite ? text.unfavorite : text.favorite}</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </li>
  );
}
